import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react'
import { StyleSheet, View } from 'react-native'
import Modal from 'react-native-modal'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { PlanLimitCode } from '@utils'
import { navigationRef } from '../navigationRef'
import { useLanguage } from './LanguageContext'
import { Icon, Text } from '../components/atoms'
import { Button } from '../components/molecules/Button'

/**
 * The reason the paywall was raised — the stable plan-limit code plus the
 * `params` the API sent (e.g. `{ limit }`), so the prompt can show a
 * specific, templated line for that limit.
 */
export interface PaywallReason {
  code?: PlanLimitCode
  params?: Record<string, unknown>
}

interface PaywallContextType {
  showPaywall: (reason?: PaywallReason) => void
  hidePaywall: () => void
}

// A no-op default keeps callers (and the axios path / tests) safe even when a
// provider is not mounted, instead of throwing mid-request.
const PaywallContext = createContext<PaywallContextType>({
  showPaywall: () => undefined,
  hidePaywall: () => undefined,
})

const durationAnimationIn = 300
const durationAnimationOut = 150

const navigateToSubscription = () => {
  if (navigationRef.isReady()) {
    navigationRef.navigate('Menu' as never, { screen: 'Subscription' } as never)
  }
}

interface PaywallProviderProps {
  children: ReactNode
}

export const PaywallProvider: React.FC<PaywallProviderProps> = ({ children }) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const { t } = useLanguage()

  const [isVisible, setVisible] = useState(false)
  const [reason, setReason] = useState<PaywallReason | undefined>(undefined)

  const showPaywall = useCallback((nextReason?: PaywallReason) => {
    setReason(nextReason)
    setVisible(true)
  }, [])

  const hidePaywall = useCallback(() => {
    setVisible(false)
  }, [])

  const onUpgrade = useCallback(() => {
    setVisible(false)
    navigateToSubscription()
  }, [])

  // The plan-limit code messages (with their {limit} params) double as the
  // prompt body, so a user learns exactly which limit they hit. Falls back to
  // the generic body when no specific code is available.
  const body = useMemo(() => {
    if (reason?.code) {
      const key = `errors.${reason.code}`
      const translated = t(key, reason.params)
      if (translated && translated !== key) {
        return translated
      }
    }
    return t('planLimit.body')
  }, [reason, t])

  const value = useMemo(() => ({ showPaywall, hidePaywall }), [showPaywall, hidePaywall])

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <Modal
        style={styles.modal}
        isVisible={isVisible}
        onBackdropPress={hidePaywall}
        animationIn="fadeIn"
        animationOut="fadeOut"
        statusBarTranslucent
        animationInTiming={durationAnimationIn}
        animationOutTiming={durationAnimationOut}
        backdropTransitionInTiming={durationAnimationIn}
        backdropTransitionOutTiming={durationAnimationOut}
      >
        <View style={styles.container}>
          <View style={styles.iconBadge}>
            <Icon name="star" size={theme.iconSize.jumbo} color={theme.colors.white} />
          </View>

          <Text type="bodySmallBold" style={styles.title}>
            {t('planLimit.title')}
          </Text>

          <Text type="bodySmall" style={styles.text}>
            {body}
          </Text>

          <View style={styles.buttonsContainer}>
            <Button
              title={t('planLimit.upgrade')}
              isGreen
              containerStyle={styles.upgradeButton}
              onPress={onUpgrade}
            />
            <Button title={t('planLimit.later')} unfilled onPress={hidePaywall} />
          </View>
        </View>
      </Modal>
    </PaywallContext.Provider>
  )
}

export const usePaywall = (): PaywallContextType => useContext(PaywallContext)

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    modal: {
      flex: 1,
      paddingHorizontal: theme.spacing.lg,
      justifyContent: 'center',
    },
    container: {
      width: '100%',
      backgroundColor: theme.colors.white,
      alignItems: 'center',
      paddingHorizontal: theme.spacing.xl,
      paddingVertical: theme.spacing.xxl,
      borderRadius: theme.border.medium,
    },
    iconBadge: {
      width: 64,
      height: 64,
      borderRadius: theme.border.jumbo * 2,
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing.lg,
    },
    title: {
      textAlign: 'center',
      marginHorizontal: theme.spacing.xl,
    },
    text: {
      textAlign: 'center',
      marginHorizontal: theme.spacing.sm,
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xl,
    },
    buttonsContainer: {
      width: '100%',
    },
    upgradeButton: {
      marginBottom: theme.spacing.md,
    },
  })
